/**
 * Download CSV from user audit export endpoints.
 * GET /user-audit-logs/export
 * GET /users/:userId/audit-trail/export
 */

const withExtension = (name, ext) => {
  const base = String(name || "")
    .replace(/\.[^.]+$/, "")
    .trim();
  return `${base || "user-audit"}.${ext}`;
};

const looksLikeCsv = (text) => {
  const firstLine =
    String(text || "")
      .split(/\r?\n/)
      .find((line) => line.trim()) || "";
  return (
    firstLine.includes(",") &&
    /date|action|description|status|actor/i.test(firstLine)
  );
};

const downloadBlob = (blob, downloadName) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = downloadName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};

export const downloadUserAuditCsv = async ({
  path,
  accessToken,
  query = {},
  fileName,
}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const url = `${base.replace(/\/$/, "")}${path}${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "ngrok-skip-browser-warning": "69420",
      Accept: "text/csv, application/octet-stream, */*",
      Authorization: `${accessToken}`,
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let message = text || `Failed to export audit trail (${response.status})`;
    try {
      const json = JSON.parse(text);
      message = json?.error || json?.message || message;
    } catch {
      /* plain text */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const buffer = await response.arrayBuffer();
  const asText = new TextDecoder().decode(buffer);

  let downloadName = fileName || "user_audit_log";
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  );
  if (match) {
    downloadName = decodeURIComponent(match[1] || match[2]).trim();
  }

  if (
    contentType.includes("text/csv") ||
    contentType.includes("application/csv") ||
    looksLikeCsv(asText)
  ) {
    const csvWithBom =
      asText.charCodeAt(0) === 0xfeff ? asText : `\uFEFF${asText}`;
    downloadBlob(
      new Blob([csvWithBom], { type: "text/csv;charset=utf-8" }),
      withExtension(downloadName, "csv")
    );
    return;
  }

  if (contentType.includes("application/json") || asText.trim().startsWith("{")) {
    let json;
    try {
      json = JSON.parse(asText);
    } catch {
      throw new Error("Server did not return a valid CSV export");
    }
    throw new Error(
      json?.error || json?.message || "Server did not return a CSV export"
    );
  }

  throw new Error("Server did not return a valid audit trail export");
};
